<template>
  <v-dialog v-model="dialogVisible" max-width="900">
    <v-card>
      <v-card-title class="text-h6 px-4 pt-4">
        {{ task?.title }}
      </v-card-title>

      <v-card-text>
        <div v-if="groupedMatches.length === 0" class="text-center py-4 text-grey">
          No subtasks defined
        </div>

        <v-expansion-panels v-else variant="accordion" multiple>
          <v-expansion-panel
            v-for="(subtask, index) in groupedMatches"
            :key="`subtask-${index}-${subtask.title}`"
            :class="getSubtaskPanelClass(subtask)"
            :disabled="subtask.items.length === 0"
          >
            <v-expansion-panel-title>
              <div class="d-flex align-center w-100">
                <span>{{ subtask.title }}</span>
                <v-spacer></v-spacer>
                <v-chip size="small" class="mr-2">
                  {{ subtask.items.length }} match{{ subtask.items.length !== 1 ? 'es' : '' }}
                </v-chip>
              </div>
            </v-expansion-panel-title>

            <v-expansion-panel-text>
              <div v-if="subtask.items.length === 0" class="text-center py-3 text-grey">
                No matches found
              </div>
              <template v-else>
                <v-alert
                  v-if="isSubtaskConflicted(subtask)"
                  density="compact"
                  variant="outlined"
                  class="mb-3"
                >
                  Selecting an item will de-select from another subtask.
                </v-alert>
                <v-table density="compact" class="items-table">
                <thead>
                  <tr>
                    <th class="text-center" style="width: 50px;"></th>
                    <th class="text-left">ID</th>
                    <th class="text-left">Title</th>
                    <th class="text-center" style="width: 120px;">Progress</th>
                    <th class="text-left">Location</th>
                    <th v-if="showLinkedColumn" class="text-center">Linked</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in subtask.items" :key="item.itemId">
                    <td class="text-center">
                      <v-checkbox
                        :model-value="selectedRadio[getSubtaskKey(subtask)] === item.itemId"
                        hide-details
                        density="compact"
                        @click.stop="toggleItemSelection(subtask, item.itemId)"
                      ></v-checkbox>
                    </td>
                    <td class="text-caption">
                      <div class="d-flex align-center">
                        <span>{{ item.itemClientId || '-' }}</span>
                        <v-btn
                          icon
                          size="x-small"
                          variant="text"
                          density="compact"
                          class="ml-1"
                          @click.stop="openItem(item)"
                        >
                          <v-icon size="small">mdi-open-in-new</v-icon>
                        </v-btn>
                      </div>
                    </td>
                    <td>{{ item.itemTitle }}</td>
                    <td class="text-center">
                      <div v-if="item.itemPercentComplete !== undefined && item.itemPercentComplete !== null" class="d-flex align-center">
                        <v-progress-linear
                          :model-value="Math.round(item.itemPercentComplete)"
                          color="primary"
                          height="16"
                          rounded
                          class="flex-grow-1"
                        >
                          <template #default>
                            <span class="text-caption font-weight-medium">{{ Math.round(item.itemPercentComplete) }}%</span>
                          </template>
                        </v-progress-linear>
                      </div>
                      <span v-else class="text-grey">-</span>
                    </td>
                    <td>
                      <div class="text-caption">{{ item.itemLocationTitle || '-' }}</div>
                      <div v-if="item.taskHierarchy" class="text-caption text-grey">{{ item.taskHierarchy }}</div>
                    </td>
                    <td v-if="showLinkedColumn" class="text-center">
                      <v-icon
                        v-if="item.isLinked"
                        color="success"
                        size="small"
                      >
                        mdi-check-circle
                      </v-icon>
                    </td>
                  </tr>
                </tbody>
              </v-table>
              </template>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="dialogVisible = false">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import { useSettingsStore } from '@/stores/settings';

const settingsStore = useSettingsStore();

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  task: {
    type: Object,
    default: null
  },
  matches: {
    type: Array,
    default: () => []
  },
  subtasks: {
    type: Array,
    default: () => []
  },
  showLinkedColumn: {
    type: Boolean,
    default: false
  },
  // Initial selections passed from parent (auto-assignments or user selections)
  // Map of subtaskKey -> itemId
  initialSelections: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['update:modelValue', 'selection-change']);

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

// Track selected radio (keyed by subtask)
const selectedRadio = ref({});

// Track original selections to detect changes
const originalSelections = ref({});

// Flag to prevent emitting during initialization
const isInitializing = ref(false);

// Group matches by subtask (title + itemType), preserving natural order from subtasks prop
// Also include unmatched subtasks (those with no matches)
const groupedMatches = computed(() => {
  const groups = {};
  const orderedKeys = [];

  // First, create groups from all subtasks to preserve their natural order
  for (const subtask of props.subtasks) {
    const key = `${subtask.title}|${subtask.itemType}`;
    if (!groups[key]) {
      groups[key] = {
        title: subtask.title || 'Unknown Subtask',
        itemType: subtask.itemType,
        items: []
      };
      orderedKeys.push(key);
    }
  }

  // Then add matches to their corresponding groups
  for (const match of props.matches) {
    const key = `${match.subtaskTitle}|${match.itemType}`;

    if (!groups[key]) {
      // Fallback for matches without a corresponding subtask
      groups[key] = {
        title: match.subtaskTitle || 'Unknown Subtask',
        itemType: match.itemType,
        items: []
      };
      orderedKeys.push(key);
    }

    groups[key].items.push(match);
  }

  // Return groups in the order they were first encountered (from subtasks)
  return orderedKeys.map(key => groups[key]);
});

// Get subtask status: 'linked', 'selected', 'multiple', 'matched', or 'unmatched'
// 'selected' means user has made a selection (includes re-linking to a different item)
const getSubtaskStatus = (subtask) => {
  const subtaskKey = getSubtaskKey(subtask);
  const linkedItem = subtask.items.find(item => item.isLinked);
  const hasLinked = !!linkedItem;

  // Check if user has made a selection for this subtask
  const currentSelection = selectedRadio.value[subtaskKey];

  // Check if user explicitly deselected (null value vs undefined/no key)
  const hasExplicitDeselection = subtaskKey in selectedRadio.value && currentSelection === null;

  // If user explicitly deselected, show as 'multiple' (orange) to indicate it needs attention
  if (hasExplicitDeselection) {
    return 'multiple';
  }

  // If there's a linked item but user selected a DIFFERENT item, it's "selected" (blue), not "linked" (green)
  if (hasLinked && currentSelection && currentSelection !== linkedItem.itemId) {
    return 'selected';
  }

  if (hasLinked) return 'linked';

  // If there are multiple matches and user has selected one
  if (subtask.items.length > 1 && currentSelection) {
    // User made a selection on a multiple-match subtask
    return 'selected';
  }

  if (subtask.items.length > 1) return 'multiple';
  if (subtask.items.length === 1) return 'matched';
  return 'unmatched';
};

// Get CSS class for subtask panel based on status
const getSubtaskPanelClass = (subtask) => {
  const status = getSubtaskStatus(subtask);
  return `subtask-panel-${status}`;
};

// Get unique key for a subtask
const getSubtaskKey = (subtask) => {
  return `${subtask.title}|${subtask.itemType}`;
};

// Get the URL to open an item in RDrive
const getItemUrl = (item) => {
  let apiUrl = settingsStore.settings.apiUrl;
  // Remove 'api-' prefix if present
  if (apiUrl.startsWith('api-')) {
    apiUrl = apiUrl.substring(4);
  }
  const projectCode = settingsStore.settings.projectCode;
  const itemType = item.itemType === 'issue' ? 'issue' : 'process';
  return `https://${apiUrl}/${projectCode}/plan-viewer/${item.drawingId}#view/${itemType}${item.itemId}`;
};

// Open item in a new tab
const openItem = (item) => {
  const url = getItemUrl(item);
  window.open(url, '_blank');
};

// Toggle item selection - single select with deselect capability
const toggleItemSelection = (subtask, itemId) => {
  const subtaskKey = getSubtaskKey(subtask);
  const currentSelection = selectedRadio.value[subtaskKey];

  if (currentSelection === itemId) {
    // Currently selected - deselect it
    selectedRadio.value[subtaskKey] = null;
  } else {
    // Not selected - select it (deselects any other item in this subtask)
    selectedRadio.value[subtaskKey] = itemId;
  }
};

// Check if a subtask is orange because all its items are assigned to other tasks
// (multiple matches but no selection available)
const isSubtaskConflicted = (subtask) => {
  const status = getSubtaskStatus(subtask);
  if (status !== 'multiple') return false;

  // If status is 'multiple', it means there are multiple items but no selection
  // This happens when all items are already assigned to other tasks
  const subtaskKey = getSubtaskKey(subtask);
  const hasSelection = !!selectedRadio.value[subtaskKey];

  // If there's no selection and we have multiple items, all items must be taken
  return !hasSelection && subtask.items.length > 1;
};

// Track if we've done the initial setup for the current task
const hasInitialized = ref(false);

// Initialize selections only when matches change (new task selected)
// Don't re-initialize when initialSelections change from parent updates
watch(() => props.matches, (newMatches) => {
  isInitializing.value = true;
  hasInitialized.value = false;
  selectedRadio.value = {};
  originalSelections.value = {};

  // Get the current initialSelections at the time of initialization
  const currentInitialSelections = props.initialSelections;

  // Group by subtask to determine auto-selection
  const subtaskGroups = {};
  for (const match of newMatches) {
    const key = `${match.subtaskTitle}|${match.itemType}`;
    if (!subtaskGroups[key]) {
      subtaskGroups[key] = [];
    }
    subtaskGroups[key].push(match);
  }

  // For each subtask, select based on priority:
  // 1. Use initialSelections from parent (auto-assignment or user selection)
  // 2. If linked item exists, select it
  // 3. If exactly 1 non-linked item, select it
  for (const [key, items] of Object.entries(subtaskGroups)) {
    const linkedItem = items.find(item => item.isLinked);

    // Check if parent provided an initial selection
    if (currentInitialSelections && currentInitialSelections[key]) {
      const initialItemId = currentInitialSelections[key];
      selectedRadio.value[key] = initialItemId;
      // Track original selection
      originalSelections.value[key] = {
        itemId: initialItemId,
        wasLinked: linkedItem?.itemId === initialItemId
      };
    } else if (linkedItem) {
      selectedRadio.value[key] = linkedItem.itemId;
      // Track original selection (linked item)
      originalSelections.value[key] = {
        itemId: linkedItem.itemId,
        wasLinked: true
      };
    } else {
      const nonLinkedItems = items.filter(item => !item.isLinked);
      if (nonLinkedItems.length === 1) {
        selectedRadio.value[key] = nonLinkedItems[0].itemId;
        // Track original selection (auto-selected single match)
        originalSelections.value[key] = {
          itemId: nonLinkedItems[0].itemId,
          wasLinked: false
        };
      }
      // If multiple non-linked items with no initialSelection, no auto-selection - user must choose
    }
  }

  // Reset initializing flag after a microtask to allow the watch to settle
  nextTick(() => {
    isInitializing.value = false;
    hasInitialized.value = true;
  });
}, { immediate: true });

// Watch for selection changes and emit them
watch(selectedRadio, (newSelections) => {
  // Skip emitting during initialization to prevent infinite loops
  if (isInitializing.value) return;
  if (!props.task) return;

  const changes = [];

  // Build a map of match details for quick lookup
  const matchMap = {};
  for (const match of props.matches) {
    const key = `${match.subtaskTitle}|${match.itemType}`;
    if (!matchMap[key]) {
      matchMap[key] = {};
    }
    matchMap[key][match.itemId] = match;
  }

  // Check each subtask for changes
  for (const [subtaskKey, selectedItemId] of Object.entries(newSelections)) {
    const original = originalSelections.value[subtaskKey];
    const originalItemId = original?.itemId || null;
    const wasLinked = original?.wasLinked || false;

    // Handle deselection (unlink) case
    if (selectedItemId === null) {
      // Only emit if there was a previous selection
      if (originalItemId) {
        const originalMatch = matchMap[subtaskKey]?.[originalItemId];
        changes.push({
          taskId: props.task.id,
          taskTitle: props.task.title,
          subtaskKey,
          subtaskTitle: originalMatch?.subtaskTitle || subtaskKey.split('|')[0],
          itemType: originalMatch?.itemType || subtaskKey.split('|')[1],
          templateCode: originalMatch?.templateCode,
          // Previous state
          previousItemId: originalItemId,
          wasLinked,
          // New state - nothing selected
          newItemId: null,
          newItemTitle: null,
          newItemClientId: null,
          // Change type
          changeType: 'unlink'
        });
      }
      continue;
    }

    // Get the selected match details
    const selectedMatch = matchMap[subtaskKey]?.[selectedItemId];
    if (!selectedMatch) continue;

    // Determine if this is a change that needs to be tracked
    const isChange = selectedItemId !== originalItemId;
    const isNewLink = !wasLinked && selectedItemId; // Was not linked, now selecting something
    const isUnlink = wasLinked && selectedItemId !== originalItemId; // Was linked, now selecting different item

    if (isChange || isNewLink) {
      changes.push({
        taskId: props.task.id,
        taskTitle: props.task.title,
        subtaskKey,
        subtaskTitle: selectedMatch.subtaskTitle,
        itemType: selectedMatch.itemType,
        templateCode: selectedMatch.templateCode,
        // Previous state
        previousItemId: originalItemId,
        wasLinked,
        // New state
        newItemId: selectedItemId,
        newItemTitle: selectedMatch.itemTitle,
        newItemClientId: selectedMatch.itemClientId,
        // Change type
        changeType: isUnlink ? 'change' : (wasLinked ? 'change' : 'link')
      });
    }
  }

  emit('selection-change', {
    taskId: props.task.id,
    changes
  });
}, { deep: true });
</script>

<style scoped>
.items-table {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 4px;
}

.items-table th {
  background-color: rgba(var(--v-theme-surface-variant), 0.4);
  font-weight: 600;
}

/* Linked subtask - green background (#4caf50) */
.subtask-panel-linked {
  background-color: rgba(76, 175, 80);
}

.subtask-panel-linked :deep(.v-expansion-panel-title) {
  background-color: rgba(76, 175, 80);
}

/* Multiple matches subtask - orange background (#ff9800) */
.subtask-panel-multiple {
  background-color: rgba(255, 152, 0);
}

.subtask-panel-multiple :deep(.v-expansion-panel-title) {
  background-color: rgba(255, 152, 0);
}

/* Single match subtask - blue background (#2196f3) */
.subtask-panel-matched {
  background-color: rgba(33, 150, 243);
}

.subtask-panel-matched :deep(.v-expansion-panel-title) {
  background-color: rgba(33, 150, 243);
}

/* User-selected subtask (from multiple matches) - blue background (#2196f3) */
.subtask-panel-selected {
  background-color: rgba(33, 150, 243);
}

.subtask-panel-selected :deep(.v-expansion-panel-title) {
  background-color: rgba(33, 150, 243);
}

/* Unmatched subtask - grey background (#9e9e9e) */
.subtask-panel-unmatched {
  background-color: rgba(158, 158, 158);
}

.subtask-panel-unmatched :deep(.v-expansion-panel-title) {
  background-color: rgba(158, 158, 158);
}
</style>
